import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, Users, BookOpen, UserCog, 
  GraduationCap, UserCheck, BookMarked, TrendingUp,
  ChevronRight, Plus, Video, BarChart3, Gamepad2,
  PenTool, FileText, Calculator, FlaskConical, Languages,
  Eye, Sparkles, ClipboardList, Calendar, Settings, CreditCard, ShieldCheck, Tag
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const AdminDashboard = () => {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/quality', label: 'Quality Assurance', icon: ShieldCheck },
    { path: '/admin/subscriptions', label: 'Subscriptions', icon: CreditCard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/groupon-signups', label: 'Groupon Sign-Ups', icon: Tag },
    { path: '/admin/enrollments', label: 'Enrollments', icon: ClipboardList },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
    { path: '/tutoring', label: 'Live Sessions', icon: Video },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/assessments', label: 'Assessments', icon: FileText },
    { path: '/admin/reports', label: 'Monthly Reports', icon: Calendar },
    { path: '/admin/settings', label: 'Settings', icon: Settings },
    { divider: true, label: 'Content Preview' },
    { path: '/admin/curriculum-browser', label: 'Curriculum Browser', icon: BookMarked },
    { path: '/admin/preview/games', label: 'Games Zone', icon: Gamepad2 },
    { path: '/admin/preview/exams', label: 'Exams', icon: PenTool },
    { path: '/admin/preview/essays', label: 'Essays', icon: Languages },
    { path: '/admin/preview/esol-videos', label: 'ESOL Videos', icon: Eye },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, usersRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/users')
        ]);
        setStats(statsRes.data);
        setRecentUsers(usersRes.data.users.slice(0, 5));
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const statCards = [
    { label: 'Total Students', value: stats?.total_students || 0, icon: GraduationCap, color: 'emerald' },
    { label: 'Total Parents', value: stats?.total_parents || 0, icon: Users, color: 'blue' },
    { label: 'Total Tutors', value: stats?.total_tutors || 0, icon: UserCheck, color: 'purple' },
    { label: 'Lessons Completed', value: stats?.total_lessons_completed || 0, icon: BookMarked, color: 'amber' },
  ];

  return (
    <ProLayout title="Admin Dashboard" sidebarItems={sidebarItems}>
      <div data-testid="admin-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => (
            <div key={stat.label} className="pro-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">{stat.label}</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Link to="/admin/users" data-testid="manage-users-link">
            <div className="pro-card group cursor-pointer border-2 border-transparent hover:border-emerald-500">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">Manage Users</h3>
                  <p className="text-sm text-slate-500">Create tutors & admins</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>

          <Link to="/admin/allocate" data-testid="allocate-subjects-link">
            <div className="pro-card group cursor-pointer border-2 border-transparent hover:border-blue-500">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">Allocate Subjects</h3>
                  <p className="text-sm text-slate-500">Assign subjects to students</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>

          <Link to="/admin/tutors" data-testid="assign-tutors-link">
            <div className="pro-card group cursor-pointer border-2 border-transparent hover:border-purple-500">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <UserCog className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">Assign Tutors</h3>
                  <p className="text-sm text-slate-500">Connect tutors with students</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>

        {/* Content Preview Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-slate-600" />
            <h2 className="font-heading text-xl font-bold text-slate-800">Preview Content</h2>
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Admin Access</span>
          </div>
          
          {/* Subject Cards */}
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <Link to="/admin/preview/subject/maths/ks2" data-testid="preview-maths">
              <div className="pro-card group cursor-pointer border-2 border-transparent hover:border-amber-500 bg-gradient-to-br from-amber-50 to-orange-50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800">Maths</h3>
                    <p className="text-sm text-slate-500">KS1 - KS4 Curriculum</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/admin/preview/subject/english/ks2" data-testid="preview-english">
              <div className="pro-card group cursor-pointer border-2 border-transparent hover:border-rose-500 bg-gradient-to-br from-rose-50 to-pink-50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-rose-500 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800">English</h3>
                    <p className="text-sm text-slate-500">KS1 - KS4 Curriculum</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/admin/preview/subject/science/ks2" data-testid="preview-science">
              <div className="pro-card group cursor-pointer border-2 border-transparent hover:border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <FlaskConical className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800">Science</h3>
                    <p className="text-sm text-slate-500">KS1 - KS4 Curriculum</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>

          {/* Other Content Cards */}
          <div className="grid md:grid-cols-5 gap-4">
            <Link to="/admin/preview/games" data-testid="preview-games">
              <div className="pro-card group cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Gamepad2 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Games Zone</h4>
                    <p className="text-xs text-slate-500">6 Educational Games</p>
                  </div>
                </div>
              </div>
            </Link>

            <Link to="/admin/preview/exams" data-testid="preview-exams">
              <div className="pro-card group cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Exams</h4>
                    <p className="text-xs text-slate-500">SATs & GCSE Papers</p>
                  </div>
                </div>
              </div>
            </Link>

            <Link to="/admin/preview/essays" data-testid="preview-essays">
              <div className="pro-card group cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <PenTool className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Essays</h4>
                    <p className="text-xs text-slate-500">AI-Marked Writing</p>
                  </div>
                </div>
              </div>
            </Link>

            <Link to="/admin/preview/esol-videos" data-testid="preview-esol">
              <div className="pro-card group cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Languages className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">ESOL</h4>
                    <p className="text-xs text-slate-500">Video Lessons</p>
                  </div>
                </div>
              </div>
            </Link>

            <Link to="/admin/preview/11plus" data-testid="preview-11plus">
              <div className="pro-card group cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">11+ Prep</h4>
                    <p className="text-xs text-slate-500">Exam Preparation</p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Users */}
        <div className="pro-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-bold text-slate-800">Recent Users</h2>
            <Link to="/admin/users">
              <Button variant="outline" size="sm" data-testid="view-all-users">
                View All
              </Button>
            </Link>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent"></div>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {recentUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="font-medium text-slate-800">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="text-slate-600">{user.email}</td>
                    <td>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize
                        ${user.role === 'admin' ? 'bg-red-100 text-red-700' : ''}
                        ${user.role === 'tutor' ? 'bg-purple-100 text-purple-700' : ''}
                        ${user.role === 'parent' ? 'bg-blue-100 text-blue-700' : ''}
                        ${user.role === 'student' ? 'bg-green-100 text-green-700' : ''}
                      `}>
                        {user.role}
                      </span>
                    </td>
                    <td className="text-slate-500 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </ProLayout>
  );
};

export default AdminDashboard;
